import { useEffect, useState } from 'react';

import type { ColumnsType } from 'antd/es/table';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import axios from 'axios';
import { useResearcher } from '../../../hooks';
import { RequestStatusToChip } from './RequestTable';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';


// SHER-363 #5 — mirror of the API's RequestLogChangeType. The log now records the
// explicit action (service added/removed, request cancelled) so the modal shows it
// instead of a repeated status.
enum RequestLogChangeType {
  STATUS_CHANGE = 0,
  SERVICE_ADDED = 1,
  SERVICE_REMOVED = 2,
  REQUEST_CANCELED = 3,
}

const CHANGE_TYPE_TRANSLATION: Record<RequestLogChangeType, string> = {
  [RequestLogChangeType.STATUS_CHANGE]: 'pages.hospital.services.request.log.status_change',
  [RequestLogChangeType.SERVICE_ADDED]: 'pages.hospital.services.request.log.service_added',
  [RequestLogChangeType.SERVICE_REMOVED]: 'pages.hospital.services.request.log.service_removed',
  [RequestLogChangeType.REQUEST_CANCELED]: 'pages.hospital.services.request.log.request_canceled',
};

interface RequestLog {
  id: number;
  previousStatus: number | null;
  newStatus: number;
  changeType: number | null;
  serviceName: string | null;
  reason: string | null;
  changedAt: string;
  changedBy: {
    id: number;
    researcher: {
      uuid: string;
    }
  }
}

export function useRequestLogs({requestId}: {requestId: number}) { 
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RequestLog[]>([]);
  const [error, setError] = useState<number | null>(null);
  const investigationUuid = useSelector((state: any) => 
    state.investigations.currentInvestigation?.uuid
);

  useEffect(() => {
    const fetchRequestLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios(`${import.meta.env.VITE_APP_API_URL}/hospital/${investigationUuid}/request/${requestId}/status`, { 
            headers: {"Authorization" : localStorage.getItem("jwt")} 
        });
        if (response.status === 200 && response.data) {
            setData(response.data.statusLogs);
        }
        else{
            throw new Error('Failed to fetch request logs');
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch requests');
            if(error.response.status === 401){
                setError(401);
            }
            
            console.error('Error fetching requests:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequestLogs();
  }, []);

  return { data, loading, error };
}

const ResearcherName = ({ uuid }: { uuid: string }) => {
  const { researcher, loading, error } = useResearcher(uuid);

  if (loading) return <span>Loading...</span>;
  if (error) return <span>Error loading researcher</span>;
  if (!researcher) return <span>{uuid}</span>;

  return <span>{researcher.name} {researcher.surnames}</span>;
};

  // SHER-363 #5: render the explicit action ("Service removed: ASAT") from
  // changeType + serviceName. Falls back to a status-change row for legacy logs.
  const renderAction = (log: RequestLog) => {
    const key = (log.changeType !== null && CHANGE_TYPE_TRANSLATION[log.changeType as RequestLogChangeType])
      || CHANGE_TYPE_TRANSLATION[RequestLogChangeType.STATUS_CHANGE];
    return (
      <span>
        <Translate id={key} />
        {log.serviceName ? `: ${log.serviceName}` : ''}
      </span>
    );
  };

const RequestLogTable = ({requestId}:{requestId:number}) => {
  const { data, loading, error } = useRequestLogs({requestId});

  const rows = data.map(log => ({
    id: log.id,
    changedAt: new Date(log.changedAt).toLocaleString(),
    changedBy: <ResearcherName uuid={log.changedBy.researcher.uuid} />,
    action: renderAction(log),
    reason: log.reason || '',
    previousStatus: log.previousStatus ? <RequestStatusToChip status={log.previousStatus}/> : null,
    newStatus: log.newStatus ? <RequestStatusToChip status={log.newStatus}/> : null
  }));

  const headCells = [
    {
      id: 'id',
      alignment: 'left',
      label: 'Log ID'
    },
    {
      id: 'changedAt',
      alignment: 'left',
      label: 'Date'
    },
    {
      id: 'changedBy',
      alignment: 'left',
      label: 'Changed By'
    },
    {
      id: 'action',
      alignment: 'left',
      label: <Translate id="pages.hospital.services.request.log.action" />
    },
    {
      id: 'reason',
      alignment: 'left',
      label: <Translate id="pages.hospital.services.request.log.reason" />
    },
    {
      id: 'previousStatus',
      alignment: 'left',
      label: 'Previous Status'
    },
    {
      id: 'newStatus',
      alignment: 'left',
      label: 'New Status'
    }
  ];

  if (error) {
    switch(error){
        case 401:
            return <div><Translate id="pages.request.error.status_log.unauthorized"/></div>;
        default:
            return <div><Translate id="general.error"/></div>
    }
    
  }

  return (
    <EnhancedTable
      rows={rows}
      headCells={headCells}
      noSelectable={true}
      titleTable="Request Logs"
      loading={loading}
      order={{
        property: 'changedAt',
        orderBy: 'desc'
      }}
    />
  );
};

export default RequestLogTable;
